import React, {
  createContext,
  useReducer,
  useContext,
  useEffect,
  useCallback,
  useState,
} from "react";
import { getItem, setItem } from "utils/CommonUtils";
import { useMutation } from "react-fetching-library";
import { USER_TYPE } from "helpers/interfaces";
import { isSuccess, successData, errorMessage } from "utils/ServiceAPIUtil";
import { useNavigate } from "react-router-dom";

export interface UserState {
  userType: USER_TYPE;
  error?: Error;
  access_token?: string;
  accountProfile?: any;
  OTPLogin: ({ phoneNumber }: { phoneNumber: string }) => Promise<any>;
  OTPSignLogin: ({ phoneNumber }: { phoneNumber: string }) => Promise<any>;
  UserVerification: ({
    phoneNumber,
    otpCode,
  }: {
    phoneNumber: string;
    otpCode: string;
  }) => Promise<any>;
  OTPSignVerification: ({
    phoneNumber,
    otpCode,
  }: {
    phoneNumber: string;
    otpCode: string;
  }) => Promise<any>;
  TokenVerification: ({ token }: { token: string }) => Promise<any>;
  logout: () => Promise<void>;
}

export const initialState: UserState = {
  userType: "INIT",
  error: undefined,
  access_token: undefined,
  OTPLogin: async () => { },
  OTPSignLogin: async () => { },
  UserVerification: async () => { },
  OTPSignVerification: async () => { },
  TokenVerification: async () => { },
  logout: async () => { },
};

export const AuthContext = createContext(initialState);

const tokenVerificationAction: any = (formValues: { token: string }) => ({
  method: "GET",
});
const otpVerificationAction: any = (formValues: {
  mobile: string;
  otp: string;
}) => ({
  method: "POST",
  endpoint: `/v1/login/verify-otp`,
  body: {
    ucc_pan: formValues.mobile,
    otp: formValues.otp,
  },
});
//////signature verify otp////////////
const otpSignVerificationAction: any = (formValues: {
  mobile: string;
  otp: string;
}) => ({
  method: "POST",
  endpoint: `/v1/Signature/verifyOTP`,
  body: {
    ucc_pan: formValues.mobile,
    otp: formValues.otp,
  },
});
///////////signature verify otp end//////////
const otpLoginAction: any = (formValues: { mobile: string }) => ({
  method: "POST",
  endpoint: `/v1/login/send-otp`,
  body: {
    ucc_pan: formValues.mobile,
  },
});
/////signature send otp ///////
// @ts-ignore
const otpSignLoginAction: any = (formValues: { mobile: string }) => ({
  method: "POST",
  endpoint: `/v1/Signature/sendOTP`,
  body: {
    ucc_pan: formValues.mobile,
  },
});
/////signature send otp end//////////
export const AuthContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { mutate: otpLoginMutate } = useMutation(otpLoginAction);
  const { mutate: otpSignLoginMutate } = useMutation(otpSignLoginAction);
  const { mutate: otpVerificationMutate } = useMutation(otpVerificationAction);
  const { mutate: otpSignVerificationMutate } = useMutation(
    otpSignVerificationAction
  );
  const { mutate: tokenVerificationMutate } = useMutation(
    tokenVerificationAction
  );

  const controlReducer = (_state: UserState, action: any): UserState => {
    switch (action.name) {
      case "LOG_IN":
        return {
          ..._state,
          userType: "LOGGED_IN",
          error: undefined,
          access_token: action.access_token,
          accountProfile: action.accountProfile,
        };
      case "LOG_IN_ERROR":
        return {
          ..._state,
          userType: "INIT",
          error: action.value,
        };
      case "LOG_OUT": {
        return {
          ..._state,
          userType: "NOT_LOGGED_IN",
          access_token: "",
          error: undefined,
        };
      }
      case "UPDATE_ACCOUNT_PROFILE": {
        return {
          ..._state,
          accountProfile: action.accountProfile,
        };
      }
      default:
        return {
          ..._state,
        };
    }
  };

  const [state, dispatch] = useReducer(controlReducer, initialState);

  useEffect(() => {
    const authCheck = async () => {
      try {
        const tokenData = await getItem("tokens");
        const clientObj = await getItem("users_detail");
        if (
          tokenData !== undefined &&
          tokenData !== null &&
          clientObj !== undefined &&
          clientObj !== null
        ) {
          const tokenObj = JSON.parse(tokenData);
          const users_detail = JSON.parse(clientObj);
          await doLogin({ tokens: tokenObj, users_detail });
        } else {
          await doLogout();
        }
      } catch {
        await doLogout();
      }
    };
    authCheck();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogin = async (response: any) => {
    if (response) {
      const access_token = response?.tokens?.access_token;
      setItem("tokens", JSON.stringify(response?.tokens));
      setItem("access_token", JSON.stringify(response?.tokens?.access_token));
      setItem("users_detail", JSON.stringify(response?.users_detail));

      const accountProfile = {
        full_name: response?.users_detail?.full_name,
        pan: response?.users_detail?.pan,
        ucc_code: response?.users_detail?.ucc_code,
        access_token: response?.tokens?.access_token,
        refresh_token: response?.tokens?.refresh_token,
      };

      dispatch({
        name: "LOG_IN",
        access_token: access_token,
        accountProfile: accountProfile,
      });
    }
  };

  const doLogout = useCallback(async () => {
    localStorage.clear();
    localStorage.removeItem("tokens");
    localStorage.removeItem("users_detail");
    dispatch({ name: "LOG_OUT" });
  }, []);

  const authActions = React.useMemo(
    () => ({
      OTPLogin: async ({ phoneNumber }: { phoneNumber: string }) => {
        const { error: mutateError, payload: otpLoginPayload } =
          await otpLoginMutate({ mobile: phoneNumber });
        if (mutateError) {
          // eslint-disable-next-line
          throw otpLoginPayload.result;
        }
        if (isSuccess(otpLoginPayload)) {
          return successData(otpLoginPayload);
        } else {
          // eslint-disable-next-line
          throw errorMessage(otpLoginPayload);
        }
      },
      OTPSignLogin: async ({ phoneNumber }: { phoneNumber: string }) => {
        const { error: mutateError, payload: otpSignLoginPayload } =
          await otpSignLoginMutate({ mobile: phoneNumber });
        if (mutateError) {
          // eslint-disable-next-line
          throw otpSignLoginPayload.result;
        }
        if (isSuccess(otpSignLoginPayload)) {
          return successData(otpSignLoginPayload);
        } else {
          // eslint-disable-next-line
          throw errorMessage(otpSignLoginPayload);
        }
      },
      UserVerification: async (resData: any) => {
        await doLogin(resData);
      },
      OTPSignVerification: async ({
        phoneNumber,
        otpCode,
      }: {
        phoneNumber: string;
        otpCode: string;
      }) => {
        const { error: mutateError, payload: otpVerifiactionPayload } =
          await otpSignVerificationMutate({
            mobile: phoneNumber,
            otp: otpCode,
          });
        if (mutateError) {
          // eslint-disable-next-line
          throw otpVerifiactionPayload.result;
        }
        if (isSuccess(otpVerifiactionPayload)) {
          const resData = successData(otpVerifiactionPayload);
          if (resData) {
            setItem("Data", JSON.stringify(resData));
          } else {
            throw "something went wrong!";
          }
        } else {
          // eslint-disable-next-line
          throw errorMessage(otpVerifiactionPayload);
        }
      },
      TokenVerification: async ({ token }: { token: string }) => {
        const { error: mutateError, payload: tokenVerifiactionPayload } =
          await tokenVerificationMutate({ token: token });
        if (mutateError) {
          // eslint-disable-next-line
          throw "something went wrong!";
        }
        if (isSuccess(tokenVerifiactionPayload)) {
          const resData = successData(tokenVerifiactionPayload);
          if (resData && resData.token) {
            await doLogin(resData);
          } else {
            throw "something went wrong!";
          }
        } else {
          // eslint-disable-next-line
          throw errorMessage(tokenVerifiactionPayload);
        }
      },
      logout: async () => {
        await doLogout();
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        ...authActions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthContext");
  }
  return context;
};